import { useState, FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, Link } from 'react-router-dom';
import { login, saveAuthData } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import type { LoginCredentials } from '../types/auth';

export default function Login() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const navigate = useNavigate();
    const { toast } = useToast();
    const queryClient = useQueryClient();

    // Mutation do TanStack Query para fazer login
    const loginMutation = useMutation({
        mutationFn: (credentials: LoginCredentials) => login(credentials),
        onSuccess: (data) => {
            // Limpa cache de outra sessao (troca de usuario na mesma aba,
            // sem reload de pagina, deixava queries como meta-users-pages-map
            // servindo dados em cache do usuario anterior)
            queryClient.clear();

            // Salvar dados no localStorage
            saveAuthData(data);

            // Mostrar toast de sucesso
            toast({
                title: "Login realizado!",
                description: `Bem-vindo, ${data.user.name}!`,
            });

            // Redirecionar para dashboard
            navigate('/dashboard');
        },
        onError: (error: any) => {
            // Mostrar toast de erro
            const errorMessage = error.response?.data?.message ||
                error.response?.data?.detail ||
                'Email ou senha incorretos';

            toast({
                variant: "destructive",
                title: "Erro no login",
                description: errorMessage,
            });
        },
    });

    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Validação básica
        if (!email || !password) {
            toast({
                variant: "destructive",
                title: "Campos obrigatórios",
                description: "Por favor, preencha email e senha.",
            });
            return;
        }

        // Fazer login
        loginMutation.mutate({ email, password });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
            <div className="w-full max-w-md p-8 space-y-6 bg-white rounded-lg shadow-md">
                <div className="text-center">
                    <h1 className="text-3xl font-bold text-gray-900">Login</h1>
                    <p className="mt-2 text-sm text-gray-600">
                        Entre com suas credenciais
                    </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="email">Email</Label>
                        <Input
                            id="email"
                            type="email"
                            placeholder="seu@email.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={loginMutation.isPending}
                        />
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="password">Senha</Label>
                        <Input
                            id="password"
                            type="password"
                            placeholder="••••••••"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            disabled={loginMutation.isPending}
                        />
                    </div>

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={loginMutation.isPending}
                    >
                        {loginMutation.isPending ? 'Entrando...' : 'Entrar'}
                    </Button>
                </form>

                <p className="text-center text-sm text-gray-600">
                    Não tem conta?{' '}
                    <Link to="/register" className="font-medium text-blue-600 hover:underline">
                        Solicitar acesso
                    </Link>
                </p>
            </div>
        </div>
    );
}